<template>
  <ModalCard
    title="Publish WorkflowVersion to WorkflowHub"
    :handle-close="handleClose"
  >
    <template #body>
      <div>
        <fieldset>
          <div class="field">
            <label class="label mb-0">WorkflowHub Instance</label>
            <p class="help mt-0 mb-2 has-text-grey">
              Select which WorkflowHub you want to publish to
            </p>
            <div class="select is-fullwidth">
              <select v-model="selectedWorkflowHubUrl">
                <option
                  v-for="ep in configEndpoints"
                  :key="ep.url"
                  :value="ep.url"
                >
                  {{ ep.name }}
                  <span v-if="!isEndpointConfigured(ep.url)">(no API key)</span>
                </option>
              </select>
            </div>
            <p v-if="!selectedInstanceConfigured" class="help is-danger">
              No API key stored for this instance.
              <router-link
                v-if="workflowHubSettingsRoute"
                :to="workflowHubSettingsRoute"
              >
                Add one in settings
              </router-link>
              .
            </p>
          </div>
          <div class="field">
            <label class="label mt-4 mb-0">WorkflowHub Team</label>
            <p class="help mt-0 mb-2 has-text-grey">
              Type a team name in the dropdown below — results will load as you
              type.
            </p>
            <SelectContainer
              :value="workflowHubProject ?? undefined"
              :multiple="false"
              :on-search="searchWorkflowHubProjects"
              track-by="id"
              label="name"
              placeholder="Search team..."
              :disabled="!selectedInstanceConfigured"
              @input="(project) => (workflowHubProject = project)"
            >
              <template #noOptions="{ query }">
                <span v-if="!query">
                  Start typing to search for WorkflowHub teams.
                </span>
                <span v-else>
                  No teams found for &ldquo;{{ query }}&rdquo;.
                </span>
              </template>
            </SelectContainer>
          </div>
        </fieldset>
      </div>
    </template>
    <template #footer>
      <div>
        <BaseButton
          size="small"
          :outlined="true"
          label="Cancel"
          @click="handleClose"
        />
        <BaseButton
          :icon="['fa-solid', 'fa-check']"
          size="small"
          variant="primary"
          :outlined="true"
          label="Publish"
          :disabled="!canPublish"
          :loading="isPublishing"
          @click="handlePublish"
        />
      </div>
    </template>
  </ModalCard>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, watch } from 'vue'

import { get } from '@/api'
import { useWorkflowHub } from '@/composables/useWorkflowHub'
import { useStore } from '@/store'

import SelectContainer from '@/components/SelectContainer.vue'

const LAST_URL_STORAGE_KEY = 'labid.lastWorkflowHubUrl'

interface WorkflowHubProject {
  id: string
  name: string
  url: string
}

export default defineComponent({
  name: 'PublishWorkflowHubModal',
  components: { SelectContainer },
  props: {
    isPublishing: { type: Boolean, default: false },
  },
  emits: ['publish', 'close'],
  setup(props, { emit }) {
    const store = useStore()
    const { configEndpoints, getOrCreateInstance } = useWorkflowHub()

    const selectedWorkflowHubUrl = ref<string>('')
    const workflowHubProject = ref<WorkflowHubProject | null>(null)

    const currentUsername = computed<string>(
      () => store.getters['auth/loggedInUser']?.username ?? '',
    )

    const workflowHubSettingsRoute = computed(() => {
      if (!currentUsername.value) return null
      return {
        name: 'userDetail',
        params: { userUsername: currentUsername.value },
        query: { configure: 'workflowhub' },
      }
    })

    function isEndpointConfigured(url: string): boolean {
      const endpoint = configEndpoints.value.find((ep) => ep.url === url)
      if (!endpoint) return false
      return getOrCreateInstance(endpoint).isConfigured
    }

    const selectedInstanceConfigured = computed(() =>
      selectedWorkflowHubUrl.value
        ? isEndpointConfigured(selectedWorkflowHubUrl.value)
        : false,
    )

    const canPublish = computed(
      () =>
        !!workflowHubProject.value &&
        selectedInstanceConfigured.value &&
        !props.isPublishing,
    )

    function pickDefaultUrl(): string {
      const endpoints = configEndpoints.value
      if (endpoints.length === 0) return ''
      const stored =
        typeof localStorage !== 'undefined'
          ? localStorage.getItem(LAST_URL_STORAGE_KEY)
          : null
      if (stored && endpoints.some((ep) => ep.url === stored)) return stored
      const production = endpoints.find((ep) => !/\bdev\b/.test(ep.url))
      return (production ?? endpoints[0]).url
    }

    onMounted(() => {
      if (!selectedWorkflowHubUrl.value) {
        selectedWorkflowHubUrl.value = pickDefaultUrl()
      }
    })

    // Endpoints load asynchronously (via useAppConfig); pick default once they arrive.
    watch(configEndpoints, (endpoints) => {
      if (!selectedWorkflowHubUrl.value && endpoints.length > 0) {
        selectedWorkflowHubUrl.value = pickDefaultUrl()
      }
    })

    // Team choices are instance-specific -- reset when switching.
    watch(selectedWorkflowHubUrl, () => {
      workflowHubProject.value = null
    })

    async function searchWorkflowHubProjects(
      query: string,
      signal: AbortSignal,
    ) {
      if (!query)
        return { status: 200, data: { results: [], total: 0, total_pages: 1 } }
      if (!selectedWorkflowHubUrl.value) return null
      try {
        const response = await get(
          `search_workflowhub_projects/?q=${encodeURIComponent(query)}&url=${encodeURIComponent(selectedWorkflowHubUrl.value)}`,
          { signal },
        )
        if (response.status === 200) {
          const results = Object.values(
            response.data.data as Record<string, unknown>,
          ).map((project) => {
            const p = project as {
              id: string
              attributes: { title: string }
              links: { self: string }
            }
            return {
              id: p.id,
              name: p.attributes.title,
              url: p.links.self,
            }
          })
          return {
            status: 200,
            data: { results, total: results.length, total_pages: 1 },
          }
        }
        return response
      } catch (err: unknown) {
        const e = err as {
          name?: string
          response?: { status?: number; data?: { message?: string } }
        }
        if (e.name === 'AbortError') return null
        if (e.response?.status === 400 && e.response.data?.message) {
          store.commit('notify', {
            type: 'error',
            message: e.response.data.message,
            isDismissable: true,
            duration: 6000,
          })
        }
        return null
      }
    }

    function handlePublish() {
      if (!canPublish.value) return
      emit('publish', {
        url: selectedWorkflowHubUrl.value,
        project: { value: workflowHubProject.value },
      })
    }

    function handleClose() {
      workflowHubProject.value = null
      emit('close')
    }

    return {
      configEndpoints,
      selectedWorkflowHubUrl,
      workflowHubProject,
      workflowHubSettingsRoute,
      isEndpointConfigured,
      selectedInstanceConfigured,
      canPublish,
      searchWorkflowHubProjects,
      handlePublish,
      handleClose,
    }
  },
})
</script>
